import { IconFromSet } from "@gettactic/components/src/icons/IconFromSet/IconFromSet";
import { DEFAULT_FONT_FAMILY } from "@gettactic/map-builder/src/components/map/defines";
import type { IconShape } from "@gettactic/map-builder/src/types";
import {
  fetchSvgText,
  toDataUri
} from "@gettactic/map-builder/src/utils/svgAssetCache";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { buildLocalShapeTransform } from "../shapeTransform";
import { CaptionText } from "./CaptionText";

export interface IconSvgProps {
  shape: IconShape;
}

/**
 * Icons render as one `<image>` with a data URI so the browser parses each
 * distinct icon markup once, however many shapes share it.
 */
export const IconSvg: React.FC<IconSvgProps> = ({ shape }) => {
  const color = shape.color || "currentColor";
  const [remoteHref, setRemoteHref] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!shape.iconUrl || shape.svgContent) {
      setRemoteHref(null);
      return;
    }
    let isCancelled = false;
    fetchSvgText(shape.iconUrl)
      .then((svgText) => {
        if (isCancelled) return;
        setRemoteHref(toDataUri(svgText.replace(/currentColor/g, color)));
      })
      .catch(() => {
        // The raw url still renders, just without the configured colour.
        if (!isCancelled) setRemoteHref(null);
      });
    return () => {
      isCancelled = true;
    };
  }, [shape.iconUrl, shape.svgContent, color]);

  const href = React.useMemo(() => {
    if (shape.svgContent) {
      return toDataUri(shape.svgContent.replace(/currentColor/g, color));
    }
    if (shape.iconUrl) return remoteHref ?? shape.iconUrl;
    const markup = ReactDOMServer.renderToStaticMarkup(
      <IconFromSet icon={shape.icon} color={shape.color} size={64} />
    );
    return toDataUri(markup);
  }, [
    shape.svgContent,
    shape.iconUrl,
    shape.icon,
    shape.color,
    color,
    remoteHref
  ]);

  return (
    <g transform={buildLocalShapeTransform(shape)}>
      <image
        href={href}
        width={shape.width}
        height={shape.height}
        preserveAspectRatio="xMidYMid meet"
        opacity={shape.locked ? 0.5 : undefined}
      />
      {shape.text ? (
        <CaptionText
          text={shape.text}
          width={shape.width}
          top={shape.height + 10}
          textAlign={shape.textAlign}
          fontSize={shape.fontSize || 20}
          fontFamily={shape.fontFamily || DEFAULT_FONT_FAMILY}
          isBold={shape.isBold}
          isItalic={shape.isItalic}
          isUnderline={shape.isUnderline}
          color={shape.textColor || "#B5B5B5"}
        />
      ) : null}
    </g>
  );
};
